/**
 * Dormers on a roof face (pure; twin of app/design/sketch/dormers.py, tested in dormers.test.ts on the same numbers).
 *
 * A dormer: kind (`gable`, `hip`, `shed`), the middle of its front wall in plan (`center`), `width` along the eave,
 * `depth` in plan up the slope, `height` (its eave above the host roof at the front wall) and the `pitch` of its own
 * roof. Its roof is the lowest of its planes; where that is above the host roof inside the width × depth box, it is
 * cut out of the host face (a hole) and split into the dormer's faces. Walls stand on the box's sides from the host
 * roof up to the dormer's roof (cheeks, the front wall, a back wall when it still stands above the host at its full
 * depth); elsewhere the roofs meet in valleys.
 */
import type { Vec2 } from "../editor/geo.ts";

export type DormerKind = "gable" | "hip" | "shed";
export const DORMER_KINDS: DormerKind[] = ["gable", "hip", "shed"];
export type P3 = [number, number, number];

export interface SkDormer {
  id: string;
  /** The host face. */
  faceId: string;
  kind: DormerKind;
  center: Vec2;
  width: number;
  depth: number;
  height: number;
  pitch: number;
}

export interface HostPlane {
  normal: P3;
  d: number;
  azimuth: number;
}

export interface DormerFace {
  key: "left" | "right" | "front" | "roof";
  points: P3[];
  pitch: number;
  azimuth: number;
}

export interface DormerGeometry {
  cut: P3[];
  faces: DormerFace[];
  walls: [P3, P3, P3, P3][];
  /** The box in plan, counter-clockwise: front right, back right, back left, front left. */
  box: Vec2[];
}

const EPS = 1e-9;
const MIN_AREA = 0.05;
export const DORMER_DEFAULTS: Record<DormerKind, Omit<SkDormer, "id" | "faceId" | "kind" | "center">> = {
  gable: { width: 2.4, depth: 3, height: 1.2, pitch: 35 },
  hip: { width: 2.4, depth: 3, height: 1.2, pitch: 30 },
  shed: { width: 3.6, depth: 3, height: 1.0, pitch: 12 },
};

export const hostZ = (h: HostPlane, x: number, y: number) => -(h.normal[0] * x + h.normal[1] * y + h.d) / h.normal[2];

function area(points: ArrayLike<number>[]): number {
  let total = 0;
  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    const q = points[(i + 1) % points.length]!;
    total += p[0]! * q[1]! - q[0]! * p[1]!;
  }
  return total / 2;
}

function clip(poly: Vec2[], a: number, b: number, c: number): Vec2[] {
  const out: Vec2[] = [];
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i]!;
    const q = poly[(i + 1) % poly.length]!;
    const fp = a + b * p[0] + c * p[1];
    const fq = a + b * q[0] + c * q[1];
    if (fp >= -EPS) out.push(p);
    if (fp >= -EPS !== fq >= -EPS && Math.abs(fp - fq) > EPS) {
      const t = fp / (fp - fq);
      out.push([p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t]);
    }
  }
  const clean: Vec2[] = [];
  for (const p of out) if (!clean.length || Math.hypot(p[0] - clean[clean.length - 1]![0], p[1] - clean[clean.length - 1]![1]) > 1e-7) clean.push(p);
  if (clean.length > 1 && Math.hypot(clean[0]![0] - clean[clean.length - 1]![0], clean[0]![1] - clean[clean.length - 1]![1]) <= 1e-7) clean.pop();
  return clean;
}

/** (up the slope, along the eave to the left looking up) unit vectors in plan. */
export function dormerFrame(azimuth: number): { up: Vec2; along: Vec2 } {
  const az = (azimuth * Math.PI) / 180;
  const up: Vec2 = [-Math.sin(az), -Math.cos(az)];
  return { up, along: [-up[1], up[0]] };
}

function planes(d: SkDormer, zEave: number): [DormerFace["key"], number, number, number][] {
  const tp = Math.tan((d.pitch * Math.PI) / 180);
  const half = d.width / 2;
  if (d.kind === "shed") return [["roof", zEave, tp, 0]];
  const out: [DormerFace["key"], number, number, number][] = [["right", zEave + half * tp, 0, tp], ["left", zEave + half * tp, 0, -tp]];
  if (d.kind === "hip") out.push(["front", zEave, tp, 0]);
  return out;
}

/** The dormer's cut, faces and walls on `host` (null when it does not rise above the roof). */
export function dormerGeometry(host: HostPlane, d: SkDormer): DormerGeometry | null {
  const { up, along } = dormerFrame(host.azimuth);
  const [cx, cy] = d.center;
  const plan = (s: number, r: number): Vec2 => [cx + s * up[0] + r * along[0], cy + s * up[1] + r * along[1]];
  const z0 = hostZ(host, cx, cy);
  const gs = hostZ(host, ...plan(1, 0)) - z0;
  const gr = hostZ(host, ...plan(0, 1)) - z0;
  const zEave = z0 + d.height;
  const half = d.width / 2;
  const box: Vec2[] = [[0, -half], [d.depth, -half], [d.depth, half], [0, half]];
  const ps = planes(d, zEave);
  let cut = box;
  for (const [, c0, cs, cr] of ps) {
    cut = clip(cut, c0 - z0, cs - gs, cr - gr);
    if (cut.length < 3) break;
  }
  if (cut.length < 3 || area(cut) < MIN_AREA) return null;
  const host3 = (s: number, r: number): P3 => [...plan(s, r), z0 + gs * s + gr * r];
  const geometry: DormerGeometry = { cut: cut.map(([s, r]) => host3(s, r)), faces: [], walls: [], box: box.map(([s, r]) => plan(s, r)) };
  const upAz = (Math.atan2(up[0], up[1]) * 180) / Math.PI;
  for (const [key, c0, cs, cr] of ps) {
    let region = cut;
    for (const [other, o0, os, or] of ps) if (other !== key) region = clip(region, o0 - c0, os - cs, or - cr);
    if (region.length < 3 || area(region) < 1e-4) continue;
    const fall: Vec2 = [-(cs * up[0] + cr * along[0]), -(cs * up[1] + cr * along[1])];
    const slope = Math.hypot(fall[0], fall[1]);
    const azimuth = ((((slope > 1e-9 ? (Math.atan2(fall[0], fall[1]) * 180) / Math.PI : upAz + 180) % 360) + 360) % 360);
    geometry.faces.push({ key, points: region.map(([s, r]) => [...plan(s, r), c0 + cs * s + cr * r] as P3), pitch: (Math.atan(slope) * 180) / Math.PI, azimuth });
  }
  const roofZ = (s: number, r: number) => Math.min(...ps.map(([, c0, cs, cr]) => c0 + cs * s + cr * r));
  for (let i = 0; i < cut.length; i++) {
    const p = cut[i]!;
    const q = cut[(i + 1) % cut.length]!;
    const onBox = (Math.abs(p[0] - q[0]) < 1e-7 && (Math.abs(p[0]) < 1e-7 || Math.abs(p[0] - d.depth) < 1e-7)) || (Math.abs(p[1] - q[1]) < 1e-7 && Math.abs(Math.abs(p[1]) - half) < 1e-7);
    if (!onBox) continue;
    const m: Vec2 = [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2];
    if ([p, q, m].every((x) => roofZ(...x) - host3(...x)[2] < 1e-4)) continue;
    geometry.walls.push([host3(...p), host3(...q), [...plan(...p), roofZ(...p)], [...plan(...q), roofZ(...q)]]);
  }
  return geometry;
}

/** A dormer whose front-wall middle is `center`, with the kind's defaults, kept inside `facePlan` when it can be. */
export function newDormer(id: string, faceId: string, kind: DormerKind, center: Vec2): SkDormer {
  return { id, faceId, kind, center, ...DORMER_DEFAULTS[kind] };
}

/** Whether the dormer's box lies inside the face's outline (plan). */
export function boxInside(box: Vec2[], ring: Vec2[]): boolean {
  const inside = ([x, y]: Vec2) => {
    let hit = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i]!;
      const [xj, yj] = ring[j]!;
      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) hit = !hit;
    }
    return hit;
  };
  return box.every(inside);
}
